import {
  selectCurrentPlayer,
  selectPendingShootingActions,
  selectSharedGameState,
  selectUnits,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {checkTutorialPredicate} from "./tutorialStepPredicates";
import {getActiveTutorialContext} from "../../configs/tutorial";
import {isPlayerFaction} from "../factions/isPlayerFaction";
import {FACTIONS} from "../../constants/factions";
import {DAMAGE_DICE_MAX_VALUE} from "../../configs/damage";

/**
 * Гейт действий режима обучения: какие экшены игрока разрешает текущий шаг
 * сценария. Используется tutorialMiddleware как жесткий инвариант — действие,
 * не разрешенное шагом, не применяется вовсе, игроку показывается info-тост
 * с текущей задачей.
 *
 * Три уровня допуска:
 * 1. SYSTEM_ALLOWED_TYPES и все типы вне DANGEROUS_ACTION_TYPES — всегда можно
 *    (приватный UI, системные каскады применения фаз).
 * 2. UNDO_ALLOWED_TYPES — отмены всегда можно: они только возвращают
 *    состояние назад, а выполнение шага детектируется по состоянию.
 * 3. Матчеры шага (step.allow) — декларации конфига вида
 *    {action: 'build', token: 'barracks', hexKey: '0,2'}.
 *
 * Действия бота проходят всегда: скрипт бота помечает свои диспатчи флагом
 * __tutorialBot, плюс страховка по currentPlayer.isBot (каскады передач хода).
 */

// Экшены, меняющие shared-состояние по прямой инициативе игрока.
// Экспортируется: этим же списком сюжетные миссии глушат действия игрока, пока
// на экране реплика персонажа (см. missionMiddleware) — список должен
// оставаться единым, иначе новая механика окажется закрыта только в одном месте
export const DANGEROUS_ACTION_TYPES = new Set([
  'SET_HEX_TOKEN',
  'SET_SPECIAL_BUILDING',
  'CLEAR_HEX',
  'SET_STARTING_QUARTER',
  'COMPLETE_PHASE',
  'START_NEXT_PHASE',
  'SET_WAR_PHASE_STAGE',
  'DISTRIBUTE_SUPPLY',
  'DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY',
  'RECRUIT_UNIT',
  'ADD_PENDING_RECRUITMENT_ACTION',
  'UNIT_UPGRADE',
  'MOVE_UNIT',
  'ADD_PENDING_MOVEMENT_ACTION',
  'ADD_PENDING_MANEUVER_ACTION',
  'ADD_PENDING_SHOOTING_ACTION',
  'REROLL_PENDING_SHOOTING_ACTION',
  'CALCULATE_MELEE_COMBAT_DAMAGE',
  'REPAIR_BUILDING',
  'BUY_POLICY',
  'CLEAR_POLICY_MARKET',
  'TOGGLE_INITIATIVE_INTERCEPTION',
  'POLICY_ISOLATIONISM_INTERCEPT_INITIATIVE',
  'POLICY_FANATICISM_CONVERT_WIN_POINTS',
  'POLICY_AGRARIANISM_TURN_ON_CONVERSION',
  'POLICY_AGRARIANISM_TURN_OFF_CONVERSION',
  'POLICY_AGRARIANISM_SELECT_FARM',
  'FACTION_LEPRECHAUNS_STEAL_COINS',
  'ELEMENTALS_SPEND_ELEMENT',
]);

// Системные экшены движка: применение фаз, загрузка, бутстрап — блокировать
// нельзя, они не являются «действием игрока», даже когда идут в его ход
const SYSTEM_ALLOWED_TYPES = new Set([
  'LOAD_STATE',
  'SET_PLAYERS',
  'START_GAME',
  'SET_GAME_TYPE',
  'SET_POLICY_MARKET',
  'SET_BOTS_PLANNER_MODE',
  'APPLY_RECRUITMENT_ACTIONS',
  'APPLY_MOVEMENT_ACTIONS',
  'APPLY_SHOOTING_ACTIONS',
  'APPLY_MANEUVER_ACTIONS',
  'APPLY_MELEE_DAMAGE',
  'CAPTURE_CITIES',
  'SET_COMBAT_RESULTS',
  'CLEAR_COMBAT_RESULTS',
  'SET_TURN_END_TIMESTAMP',
  'SET_EXTENDED_GAME_RESULTS',
  'SEND_CONSTRUCTION_VALIDATION_MESSAGE',
  'SET_INVALID_PENDING_BUILDINGS',
  'POLICY_AGRARIANISM_COMMIT_CONVERSION',
  'CONFIRM_SETUP_PHASE',
  'CONFIRM_COMBAT_STAGE',
  // Добор случайной карты в рынок — парный диспатч ПОСЛЕ покупки политики:
  // шаг к этому моменту уже мог продвинуться (предикат покупки выполнен),
  // и блокировать добор нельзя — рынок остался бы неполным
  'ADD_NEW_POLICY_TO_POLICY_MARKET',
  'ELEMENTALS_SET_RANDOM_ELEMENTS',
  'RESET_STATE',
]);

// Отмены разрешены всегда (см. док-комментарий)
const UNDO_ALLOWED_TYPES = new Set([
  'UNDO_PENDING_SUPPLY_ACTION',
  'UNDO_UNIT_PENDING_SUPPLY',
  'UNDO_PENDING_RECRUITMENT_ACTION',
  'UNDO_PENDING_MOVEMENT_ACTION',
  'UNDO_PENDING_MANEUVER_ACTION',
  'UNDO_PENDING_BUILDING',
  'UNDO_REPAIR_BUILDING',
]);

// Каскад «завершить ход/этап»: одна декларация {action: 'completeTurn'}
// разрешает все экшены, которые диспатчит handleCompleteTurn
const COMPLETE_TURN_CASCADE_TYPES = new Set([
  'COMPLETE_PHASE',
  'START_NEXT_PHASE',
  'SET_WAR_PHASE_STAGE',
]);

const matchOptional = (expected, actual) => expected === undefined || expected === actual;

// Сколько костей перебрасывает экшен переброса ближнего боя
const countRerollDice = ({isDice1Selected, isDice2Selected} = {}) => Number(Boolean(isDice1Selected))
  + Number(Boolean(isDice2Selected));

/**
 * Сколько костей шаг обучения требует перебросить при ТЕКУЩЕМ броске отряда.
 * Кость с максимальной гранью выбрать для переброса нельзя (см. DamageDice),
 * поэтому требование не больше числа костей, которые еще есть куда улучшить:
 * иначе при выпавшей лучшей грани глава запиралась бы — переброс одной кости не
 * пропускается, а выбрать вторую невозможно.
 * @returns {number|null} null — шаг число костей не требует
 */
export const resolveTutorialRerollDiceCount = (requiredCount, diceRoll1, diceRoll2) => {
  if (!requiredCount) {
    return null;
  }
  const rerollableDiceCount = [diceRoll1, diceRoll2]
    .filter((diceRoll) => diceRoll < DAMAGE_DICE_MAX_VALUE)
    .length;
  return Math.min(requiredCount, rerollableDiceCount);
};

/**
 * Станет ли это снабжение ДОПОЛНИТЕЛЬНЫМ: у отряда уже есть паёк в этой фазе.
 * Правило повторяет редьюсер DISTRIBUTE_SUPPLY — у великанов повторный паёк
 * остаётся базовым, поэтому дополнительным не считается
 */
export const checkIfActionGivesAdditionalSupply = (action, state) => {
  const gameState = selectSharedGameState(state);
  const currentPlayer = gameState.players?.[gameState.currentPlayerIndex];
  if (currentPlayer && isPlayerFaction(currentPlayer, FACTIONS.GIANTS)) {
    return false;
  }

  const unitId = action.payload?.unitId;
  if (!unitId) {
    return false;
  }

  return (gameState.pendingSupply || []).some((supply) => supply.unitId === unitId);
};

/**
 * Матчеры деклараций step.allow: имя -> (action, state, params) => boolean
 */
const TUTORIAL_ACTION_MATCHERS = {
  any: () => true,

  build: (action, state, {token, hexKey}) =>
    action.type === 'SET_HEX_TOKEN'
    && matchOptional(token, action.payload?.tokenType)
    && matchOptional(hexKey, action.payload?.key),

  buildSpecial: (action, state, {token, hexKey}) =>
    action.type === 'SET_SPECIAL_BUILDING'
    && matchOptional(token, action.payload?.buildingType)
    && matchOptional(hexKey, action.payload?.hexKey),

  placeStartingQuarter: (action, state, {hexKey}) =>
    action.type === 'SET_STARTING_QUARTER' && matchOptional(hexKey, action.payload?.hexKey),

  recruitUnit: (action, state, {unitType, hexKey}) => {
    if (action.type === 'RECRUIT_UNIT') {
      return matchOptional(unitType, action.payload?.unitType) && matchOptional(hexKey, action.payload?.hexKey);
    }
    // Парная запись найма в историю действий этапа
    return action.type === 'ADD_PENDING_RECRUITMENT_ACTION'
      && action.payload?.actionType === 'UNIT_RECRUITMENT'
      && matchOptional(hexKey, action.payload?.quarterHexKey);
  },

  // unitHexKey — докупать можно только отряду на этой ячейке (шаг «докупи
  // кавалерии», когда на поле есть и другие свои отряды на казармах)
  sizeRecruitment: (action, state, {quarterHexKey, unitHexKey}) => {
    if (action.type !== 'ADD_PENDING_RECRUITMENT_ACTION'
      || action.payload?.actionType !== 'SIZE_RECRUITMENT'
      || !matchOptional(quarterHexKey, action.payload?.quarterHexKey)) {
      return false;
    }
    if (unitHexKey) {
      const unit = (selectUnits(state) || []).find((u) => u.id === action.payload?.unitId);
      return Boolean(unit) && unit.hexKey === unitHexKey;
    }
    return true;
  },

  upgradeUnit: (action) => action.type === 'UNIT_UPGRADE',

  moveUnit: (action, state, {fromHexKey, toHexKey}) => {
    if (action.type === 'MOVE_UNIT') {
      return matchOptional(toHexKey, action.payload?.newHexKey);
    }
    return action.type === 'ADD_PENDING_MOVEMENT_ACTION'
      && matchOptional(fromHexKey, action.payload?.fromHexKey)
      && matchOptional(toHexKey, action.payload?.toHexKey);
  },

  maneuverUnit: (action, state, {fromHexKey, toHexKey}) => {
    if (action.type === 'MOVE_UNIT') {
      return matchOptional(toHexKey, action.payload?.newHexKey);
    }
    return action.type === 'ADD_PENDING_MANEUVER_ACTION'
      && matchOptional(fromHexKey, action.payload?.fromHexKey)
      && matchOptional(toHexKey, action.payload?.toHexKey);
  },

  // Снабжение отряда. ВТОРОЙ паёк тому же отряду — это уже дополнительное
  // снабжение (см. giveAdditionalSupply в редьюсере DISTRIBUTE_SUPPLY): оно
  // тратит дефицитный паёк и в обучении ломает сценарий (отмена действий
  // игроку недоступна). Пропускаем его, только если шаг сам этого ждёт —
  // {action: 'supply', unitHexKey, additional: true}
  supply: (action, state, {farmHexKey, unitHexKey, additional = false}) => {
    if (action.type !== 'DISTRIBUTE_SUPPLY') {
      return false;
    }
    if (!matchOptional(farmHexKey, action.payload?.farmHexKey)
      || !matchOptional(unitHexKey, action.payload?.unitHexKey)) {
      return false;
    }
    return additional || !checkIfActionGivesAdditionalSupply(action, state);
  },

  // Доп. снабжение великанов — отдельным действием; в обучении великанов нет,
  // но матчер нужен, чтобы шаг мог его разрешить явно
  giantsAdditionalSupply: (action) => action.type === 'DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY',

  shoot: (action, state, {targetHexKey}) =>
    action.type === 'ADD_PENDING_SHOOTING_ACTION'
    && matchOptional(targetHexKey, action.payload?.targetHexKey),

  shootReroll: (action) => action.type === 'REROLL_PENDING_SHOOTING_ACTION',

  // Взаимодействия этапа ближнего боя: смена приоритетной цели, переброс.
  // rerollDiceCount — сценарий ждет переброса именно стольких костей (глава 3:
  // обе кости штурма). Переброс меньшего числа костей не пропускается: урон
  // вышел бы меньше задуманного, и сценарий пошел бы дальше с ним. Кости с
  // максимальной гранью в требование не входят (resolveTutorialRerollDiceCount)
  meleeInteraction: (action, state, {rerollDiceCount}) => {
    if (action.type !== 'CALCULATE_MELEE_COMBAT_DAMAGE') {
      return false;
    }
    if (!rerollDiceCount || !action.payload?.isReroll) {
      return true;
    }
    const meleeEntry = (selectSharedGameState(state).pendingMeleeDamage || [])
      .find((damageAction) => damageAction.unitId === action.payload.rerollUnitId);
    const requiredDiceCount = meleeEntry
      ? resolveTutorialRerollDiceCount(rerollDiceCount, meleeEntry.diceRoll1, meleeEntry.diceRoll2)
      : rerollDiceCount;
    return countRerollDice(action.payload) >= requiredDiceCount;
  },

  repairBuilding: (action, state, {hexKey}) =>
    action.type === 'REPAIR_BUILDING' && matchOptional(hexKey, action.payload?.hexKey),

  buyPolicy: (action, state, {policyId}) => {
    // Добор новой карты в рынок — парный диспатч покупки
    if (action.type === 'ADD_NEW_POLICY_TO_POLICY_MARKET') {
      return true;
    }
    if (action.type !== 'BUY_POLICY') {
      return false;
    }
    if (policyId === undefined) {
      return true;
    }
    const policyMarket = selectSharedGameState(state).policyMarket || [];
    const index = action.payload?.index;
    return policyMarket[index]?.id === policyId;
  },

  interceptInitiative: (action) => action.type === 'TOGGLE_INITIATIVE_INTERCEPTION',

  // requireObjective — не дать уйти с этапа, не сделав то, ради чего шаг и
  // существует. Завершение хода на таких шагах приходится разрешать (без него
  // не перейти с перемещения на стрельбу), но без условия игрок пропускает
  // задачу — и запирает сценарий: шаг не завершится уже никогда, а бот в своей
  // фазе ждет шага, до которого сценарию не дойти.
  //
  // Условие читается из самого шага, дублировать его в allow не нужно:
  //  - цель уже достигнута (предикат шага истинен) — пускаем. Перемещения
  //    засчитываются сразу при постановке в очередь, поэтому шагам-переходам
  //    хватает одного этого;
  //  - выстрел применяется только В МОМЕНТ завершения этапа, до этого он
  //    лежит в очереди, а предикат еще ложен — поэтому у шагов со стрельбой
  //    отдельно засчитывается выстрел по цели из их же allow.
  completeTurn: (action, state, {requireObjectiveInStage}, step) => {
    if (!COMPLETE_TURN_CASCADE_TYPES.has(action.type)) {
      return false;
    }
    // Условие действует ровно на том этапе, где цель шага и достижима.
    // Привязка к этапу обязательна: на перемещении цель «выстрелить» еще
    // невыполнима, и запрет там запер бы игрока с другой стороны — он не
    // смог бы даже дойти до стрельбы
    if (!requireObjectiveInStage
      || !step?.complete
      || selectWarPhaseStage(state) !== requireObjectiveInStage
    ) {
      return true;
    }
    // Цель достигнута: перемещения засчитываются сразу при постановке в
    // очередь, поэтому шагам-переходам хватает одной этой проверки
    if (checkTutorialPredicate(state, step.complete)) {
      return true;
    }
    // Выстрел применяется только В МОМЕНТ завершения этапа — до тех пор
    // предикат шага ложен, и засчитываем сам выстрел из очереди по цели,
    // объявленной в allow этого же шага
    const shotTargets = (step.allow || [])
      .filter((declaration) => declaration.action === 'shoot' && declaration.targetHexKey)
      .map((declaration) => declaration.targetHexKey);
    return (selectPendingShootingActions(state) || [])
      .some((pendingShot) => shotTargets.includes(pendingShot?.targetHexKey));
  },
};

/**
 * Применится ли действие с учетом гейта обучения — для UI: звук/анимацию
 * действия, которое гейт заблокирует, проигрывать нельзя. Проверяется ДО
 * диспатча (после успешного применения шаг мог продвинуться, и то же действие
 * на новом шаге уже считалось бы запрещенным). Вне обучения — всегда true.
 * @returns {boolean}
 */
export const checkTutorialActionWouldApply = (state, action) => {
  const context = getActiveTutorialContext(state);
  if (!context.chapter || context.isCompleted) {
    return true;
  }
  return checkTutorialActionAllowed(state, action, context.step);
};

/**
 * Сколько костей ближнего боя текущий шаг обучения требует перебросить
 * ({action: 'meleeInteraction', rerollDiceCount}) — для UI: панель боя
 * объясняет ошибку до броска, а не упирается в гейт. null — не требует (или
 * обучение не идет).
 * @returns {number|null}
 */
export const getTutorialRequiredMeleeRerollDiceCount = (state) => {
  const context = getActiveTutorialContext(state);
  if (!context.chapter || context.isCompleted || !context.step) {
    return null;
  }
  const counts = (context.step.allow || [])
    .filter((declaration) => declaration.action === 'meleeInteraction' && declaration.rerollDiceCount)
    .map((declaration) => declaration.rerollDiceCount);
  return counts.length > 0 ? Math.max(...counts) : null;
};

/**
 * Разрешено ли действие текущим шагом обучения.
 * @returns {boolean}
 */
export const checkTutorialActionAllowed = (state, action, step) => {
  if (action.__tutorialBot) {
    return true;
  }
  if (SYSTEM_ALLOWED_TYPES.has(action.type)) {
    return true;
  }
  if (!DANGEROUS_ACTION_TYPES.has(action.type)) {
    return true;
  }
  if (UNDO_ALLOWED_TYPES.has(action.type)) {
    return true;
  }

  const currentPlayer = selectCurrentPlayer(state);
  if (currentPlayer?.isBot) {
    // Страховка для каскадов передачи хода и незафлагованных диспатчей бота
    return true;
  }

  // Расчет боя, приходящий из каскада завершения этапа (не переброс), — не
  // прямое действие игрока: пропускаем всегда, иначе разрешенный completeTurn
  // рвался бы на входе в этап ближнего боя
  if (action.type === 'CALCULATE_MELEE_COMBAT_DAMAGE' && !action.payload?.isReroll) {
    return true;
  }

  const allowList = step?.allow || [];
  return allowList.some((declaration) => {
    const matcher = TUTORIAL_ACTION_MATCHERS[declaration.action];
    if (!matcher) {
      console.error(`[Tutorial] Unknown allow matcher: ${declaration.action}`);
      return false;
    }
    return matcher(action, state, declaration, step);
  });
};
